/** Max edge length for stored icon (client-side before save). */
export const DATA_SOURCE_ICON_MAX_STORED_PX = 64;

/** Icon size in Data menu contextual items (match imageProps + itemProps.styles.icon). */
export const DATA_SOURCE_ICON_MENU_PX = 32;

/**
 * Fluent ContextualMenu defaults `.ms-ContextualMenu-icon` to ~16px wide; larger `imageProps`
 * are clipped unless this is merged into the menu item via `itemProps`.
 * @returns Partial itemProps for `IContextualMenuItem` (`styles.icon` widens the icon slot).
 */
export function getDataSourceIconContextualMenuItemProps() {
  const px = DATA_SOURCE_ICON_MENU_PX;
  return {
    styles: {
      icon: {
        width: px,
        minWidth: px,
        maxWidth: px,
        height: px,
        maxHeight: Math.max(40, px + 4),
        fontSize: px,
        lineHeight: `${px}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        selectors: {
          '.ms-Icon-imageContainer': {
            width: px,
            height: px,
            maxWidth: px,
            maxHeight: px,
            overflow: 'visible',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          img: {
            maxWidth: px,
            maxHeight: px,
            objectFit: 'contain',
          },
          '@media (max-width: 1023px)': {
            width: px,
            minWidth: px,
            maxWidth: px,
            fontSize: px,
          },
        },
      },
    },
  };
}

/** File extensions allowed for data source icons (lowercase, no dot). */
export const DATA_SOURCE_ICON_EXTENSIONS = ['jpeg', 'jpg', 'gif', 'png', 'svg', 'webp'];

export function dataSourceIconFileExtension(filename) {
  const match = /\.([a-z0-9]+)$/i.exec(filename || '');
  return match ? match[1].toLowerCase() : '';
}

export function isDataSourceIconExtensionAllowed(ext) {
  return DATA_SOURCE_ICON_EXTENSIONS.includes(ext);
}

function sniffDataSourceIconMimeFromRawBase64(b64) {
  const cleaned = `${b64}`.replace(/\s/g, '');
  let binary;
  try {
    binary = atob(cleaned);
  } catch {
    return 'image/png';
  }
  if (binary.length < 4) {
    return 'image/png';
  }
  const b0 = binary.charCodeAt(0);
  const b1 = binary.charCodeAt(1);
  const b2 = binary.charCodeAt(2);
  const b3 = binary.charCodeAt(3);
  if (b0 === 0x89 && b1 === 0x50 && b2 === 0x4e && b3 === 0x47) {
    return 'image/png';
  }
  if (b0 === 0xff && b1 === 0xd8 && b2 === 0xff) {
    return 'image/jpeg';
  }
  if (b0 === 0x47 && b1 === 0x49 && b2 === 0x46 && b3 === 0x38) {
    return 'image/gif';
  }
  if (b0 === 0x52 && b1 === 0x49 && b2 === 0x46 && b3 === 0x46 && binary.length >= 12) {
    const w0 = binary.charCodeAt(8);
    const w1 = binary.charCodeAt(9);
    const w2 = binary.charCodeAt(10);
    const w3 = binary.charCodeAt(11);
    if (w0 === 0x57 && w1 === 0x45 && w2 === 0x42 && w3 === 0x50) {
      return 'image/webp';
    }
  }
  let head = binary.slice(0, 512);
  if (head.charCodeAt(0) === 0xfeff) {
    head = head.slice(1);
  }
  head = head.trimStart();
  if (head.startsWith('<')) {
    return 'image/svg+xml';
  }
  return 'image/png';
}

/**
 * Value suitable for <img src>. Accepts full data URLs or raw base64 (sniffs PNG/JPEG/GIF/WebP/SVG).
 */
export function dataSourceIconSrc(icon) {
  if (!icon || typeof icon !== 'string') {
    return undefined;
  }
  const trimmed = icon.trim();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.startsWith('data:')) {
    return trimmed;
  }
  const mime = sniffDataSourceIconMimeFromRawBase64(trimmed);
  return `data:${mime};base64,${trimmed.replace(/\s/g, '')}`;
}

function rasterizeDataSourceIconToMaxDataUrl(file, maxDim) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const nw = img.naturalWidth || img.width;
      const nh = img.naturalHeight || img.height;
      if (!nw || !nh) {
        reject(new Error('Invalid image dimensions'));
        return;
      }
      const scale = Math.min(1, maxDim / nw, maxDim / nh);
      const tw = Math.max(1, Math.round(nw * scale));
      const th = Math.max(1, Math.round(nh * scale));
      const canvas = document.createElement('canvas');
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not create canvas'));
        return;
      }
      ctx.drawImage(img, 0, 0, tw, th);
      const ext = dataSourceIconFileExtension(file.name);
      if (ext === 'jpg' || ext === 'jpeg') {
        resolve(canvas.toDataURL('image/jpeg', 0.92));
        return;
      }
      if (ext === 'webp') {
        try {
          const webpDataUrl = canvas.toDataURL('image/webp', 0.9);
          if (webpDataUrl.startsWith('data:image/webp')) {
            resolve(webpDataUrl);
            return;
          }
        } catch {
          /* fall through to PNG */
        }
      }
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image'));
    };
    img.src = url;
  });
}

/**
 * Reads an icon file, validates extension, resizes rasters (and vector SVG via bitmap) to max edge DATA_SOURCE_ICON_MAX_STORED_PX.
 * @returns {Promise<{ dataUrl?: string, error?: string }>}
 */
export async function processDataSourceIconFile(file) {
  if (!file) {
    return { error: 'No file selected' };
  }
  const ext = dataSourceIconFileExtension(file.name);
  if (!isDataSourceIconExtensionAllowed(ext)) {
    return {
      error: `Unsupported type (.${ext || '?'}). Allowed: ${DATA_SOURCE_ICON_EXTENSIONS.join(', ')}.`,
    };
  }
  try {
    const dataUrl = await rasterizeDataSourceIconToMaxDataUrl(file, DATA_SOURCE_ICON_MAX_STORED_PX);
    return { dataUrl };
  } catch (e) {
    return { error: e.message || 'Could not process image' };
  }
}

export function dataSourceIconAcceptAttribute() {
  return DATA_SOURCE_ICON_EXTENSIONS.map((x) => `.${x}`).join(',');
}
